import React, { useState, useEffect, useCallback} from 'react';
import axios from 'axios';
import './Contacts.css';
import SearchBar from '../components/SearchBar';

const Contacts = () => {
    const [contacts, setContacts] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showAddContactForm, setShowAddContactForm] = useState(false);
    const [showEditContactForm, setShowEditContactForm] = useState(false);
    const [selectedContact, setSelectedContact] = useState(null);

    const [contactName, setContactName] = useState('');
    const [contactEmail, setContactEmail] = useState('');
    const [contactPhone, setContactPhone] = useState('');
    const API_URL = process.env.REACT_APP_API_URL;

    const fetchContacts = useCallback(async () => {
        try {
            const token = localStorage.getItem('token'); // Retrieve the token from localStorage

            const response = await axios.get(`${API_URL}/api/contacts`, {
                headers: {
                    'Authorization': `Bearer ${token}`  // Include the token in the Authorization header
                }
            });

            setContacts(response.data);  // Update the state with the fetched contacts
        } catch (error) {
            console.error('Error fetching contacts:', error);
        }

    }, [API_URL]);

    useEffect(() => {
        if (API_URL) {
            fetchContacts();
        }
    }, [API_URL, fetchContacts]);
    
    const handleSearch = (query) => {
        setSearchQuery(query);
    };

    const handleShowAddContactForm = () => {
        setContactName('');
        setContactEmail('');
        setContactPhone('');
        setShowAddContactForm(true);
    };

    const handleAddContact = async () => {
        try {
            const token = localStorage.getItem('token');
            
            await axios.post(`${API_URL}/api/dashboard/contacts`, {
                name: contactName,
                email: contactEmail,
                phone: contactPhone
            }, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            fetchContacts();
            setShowAddContactForm(false);
            setContactName('');
            setContactEmail('');
            setContactPhone('');
        } catch (error) {
            console.error('Error executing add workflow sequence:', error);
        }
    };
    
    const handleEditContact = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API_URL}/api/contacts/${selectedContact.id}`, {
                name: contactName,
                email: contactEmail,
                phone: contactPhone,
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            fetchContacts();
            setShowEditContactForm(false);
            setSelectedContact(null);
        } catch (error) {
            console.error('Error updating contact:', error);
        }
    };

    const handleDeleteContact = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_URL}/api/contacts/${selectedContact.id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            fetchContacts();
            setShowEditContactForm(false);
            setSelectedContact(null);
        } catch (error) {
            console.error('Error deleting contact:', error);
        }
    };

    const exportContactsToCSV = () => {
        const headers = "Name,Email,Phone\n";
        const csvData = contacts.map(contact => `${contact.name},${contact.email},${contact.phone}`).join('\n');
        const csvContent = headers + csvData;

        // Create a link element to trigger the download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'contacts.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link); // Clean up the DOM after triggering the download
    };

    const filteredContacts = contacts.filter(contact =>
        contact.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="contacts-page-content">
            <div className="search-and-actions-wrapper">
                <h2>Contacts</h2>
                <div className="search-bar-container">
                    <SearchBar onSearch={handleSearch} />
                    <button 
                        className="add-contact-button" 
                        onClick={handleShowAddContactForm}
                    >
                        +
                    </button>
                </div>
            </div>

            {/* ==================== CONTACTS TABLE ==================== */}
            <div className="table-container">
                <table className="contacts-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Email Address</th>
                            <th>Phone Number</th>
                            <th>Created At</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredContacts.map(contact => (
                            <tr 
                                key={contact.id} 
                                className="contact-row"
                                onClick={() => {
                                    setSelectedContact(contact);
                                    setContactName(contact.name);
                                    setContactEmail(contact.email);
                                    setContactPhone(contact.phone);
                                    setShowEditContactForm(true);
                                }}
                            >
                                <td className="contact-name-cell">
                                    <span className="contact-avatar">
                                        {contact.name ? contact.name.charAt(0).toUpperCase() : '?'}
                                    </span>
                                    {contact.name}
                                </td>
                                <td>{contact.email || '—'}</td>
                                <td>{contact.phone || '—'}</td>
                                <td className="date-cell">
                                    {contact.created_at ? new Date(contact.created_at).toLocaleDateString('en-GB', {
                                        day: 'numeric',
                                        month: 'short',
                                        year: 'numeric'
                                    }) : '—'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {filteredContacts.length === 0 && (
                    <div className="no-contacts-state">No contacts found.</div>
                )}
            </div>

            {/* ==================== ADD CONTACT POPUP ==================== */}
            {showAddContactForm && (
                <div className="popup" onClick={() => setShowAddContactForm(false)}>
                    <div className="popup-content" onClick={(e) => e.stopPropagation()}>
                        <h3>Add New Contact</h3>
                        <form onSubmit={(e) => {
                            e.preventDefault();
                            handleAddContact();
                        }}>
                            <label>Name</label>
                            <input 
                                type="text" 
                                placeholder="John Doe" 
                                value={contactName} 
                                onChange={(e) => setContactName(e.target.value)} 
                                required 
                            />
                            
                            <label>Email Address</label>
                            <input 
                                type="email" 
                                placeholder="john@example.com" 
                                value={contactEmail} 
                                onChange={(e) => setContactEmail(e.target.value)} 
                            />
                            
                            <label>Phone Number</label>
                            <input 
                                type="tel" 
                                placeholder="+44 7123 456789" 
                                value={contactPhone} 
                                onChange={(e) => setContactPhone(e.target.value)} 
                            />
                            
                            <button type="submit">Save Contact</button>
                            <button type="button" onClick={() => setShowAddContactForm(false)}>Cancel</button>
                        </form>
                    </div>
                </div>
            )}

            {/* ==================== EDIT/DELETE CONTACT POPUP ==================== */}
            {showEditContactForm && (
                <div className="popup" onClick={() => setShowEditContactForm(false)}>
                    <div className="popup-content" onClick={(e) => e.stopPropagation()}>
                        <h3>Edit Contact</h3>
                        <form onSubmit={(e) => {
                            e.preventDefault();
                            handleEditContact();
                        }}>
                            <label>Name</label>
                            <input 
                                type="text" 
                                placeholder="Name" 
                                value={contactName} 
                                onChange={(e) => setContactName(e.target.value)} 
                                required 
                            />
                            
                            <label>Email Address</label>
                            <input 
                                type="email" 
                                placeholder="Email" 
                                value={contactEmail} 
                                onChange={(e) => setContactEmail(e.target.value)} 
                            />
                            
                            <label>Phone Number</label>
                            <input 
                                type="tel" 
                                placeholder="Phone Number" 
                                value={contactPhone} 
                                onChange={(e) => setContactPhone(e.target.value)} 
                            />
                            
                            <button type="submit">Update Details</button>
                            <button 
                                type="button" 
                                style={{ backgroundColor: 'red' }} 
                                onClick={handleDeleteContact}
                            >
                                Delete Contact
                            </button>
                            <button type="button" onClick={() => setShowEditContactForm(false)}>Cancel</button>
                        </form>
                    </div>
                </div>
            )}

            {/* ==================== EXPORT SYSTEM ==================== */}
            <div className="export-button-container">
                <button className="export-contacts-button" onClick={exportContactsToCSV}>
                    Export Contacts as CSV
                </button>
            </div>
        </div>
    );
};

export default Contacts;
